import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendUp, Wallet, ChartLine, Clock, ArrowsClockwise, GameController, Share } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { formatCurrency, formatPercent } from '../lib/utils';
import { useCurrency } from '../context/CurrencyContext';
import api from '../lib/api';
import Navbar from '../components/Navbar';
import StockCard from '../components/StockCard';
import RealTimeProfitTracker from '../components/RealTimeProfitTracker';
import PortfolioTable from '../components/PortfolioTable';
import HoldingsNewsFeed from '../components/HoldingsNewsFeed';
import DailyLearningCard from '../components/DailyLearningCard';
import OnboardingModal from '../components/OnboardingModal';
import DailyLessonModal from '../components/DailyLessonModal';
import AIAnalystPromptCard from '../components/AIAnalystPromptCard';
import AIAnalystActivationModal from '../components/AIAnalystActivationModal';
import AIAnalystPanel from '../components/AIAnalystPanel';
import AnimatedCounter from '../components/AnimatedCounter';
import MarketStatusWidget from '../components/MarketStatusWidget';
import TopMoversWidget from '../components/TopMoversWidget';
import QuickActions from '../components/QuickActions';
import PriceUpdateIndicator from '../components/PriceUpdateIndicator';
import TeenVestLoader from '../components/TeenVestLoader';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 5)  return 'Up late';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 22) return 'Good evening';
  return 'Good night';
};

const Dashboard = () => {
  const { user } = useAuth();
  const { format: fmtCcy } = useCurrency();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [topStocks, setTopStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [sharingPortfolio, setSharingPortfolio] = useState(false);
  const [aiStatus, setAiStatus] = useState(null);    // {status, sectors, reasoning, dismissed}
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [showAiModal, setShowAiModal] = useState(false);

  // Pull AI Analyst status (and cached analysis if active)
  useEffect(() => {
    let cancelled = false;
    const fetchAi = async () => {
      if (!user || user.is_guest) return;
      try {
        const s = await api.get('/ai-analyst/status');
        if (cancelled) return;
        setAiStatus(s.data);
        if (s.data?.status === 'active') {
          try {
            const a = await api.get('/ai-analyst/analysis');
            if (!cancelled) setAiAnalysis(a.data?.analysis || null);
          } catch { /* swallow; will recompute on user action */ }
        }
      } catch {
        /* ignore — non-blocking */
      }
    };
    fetchAi();
    return () => { cancelled = true; };
  }, [user?.id]);

  const handleAiActivated = (analysis) => {
    setAiStatus((prev) => ({ ...(prev || {}), status: 'active' }));
    setAiAnalysis(analysis);
  };

  const handleAiDismiss = async () => {
    setAiStatus((prev) => ({ ...(prev || {}), dismissed: true }));
    try {
      await api.post('/ai-analyst/dismiss');
    } catch {
      /* fire-and-forget */
    }
  };

  const showAiPrompt = (
    !!aiStatus &&
    aiStatus.status !== 'active' &&
    !aiStatus.dismissed &&
    !user?.is_guest
  );

  const handleSharePortfolio = async () => {
    if (user?.is_guest) {
      toast.error('Sign up free to share your portfolio');
      return;
    }
    try {
      setSharingPortfolio(true);
      // Check intro requirement first — if needed, send the user to /community
      const status = await api.get('/community/intro-status');
      if (status.data?.intro_required) {
        toast.message('Quick step first — post your community intro!');
        navigate('/community');
        return;
      }
      await api.post('/community/posts', {
        type: 'portfolio',
        content: `Here's a snapshot of my TeenVest portfolio. Roast it, rate it, or copy me — what do you think?`,
      });
      toast.success('Portfolio shared to the community feed 🎉');
      navigate('/community');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not share portfolio';
      toast.error(typeof msg === 'string' ? msg : 'Could not share portfolio');
    } finally {
      setSharingPortfolio(false);
    }
  };

  // Onboarding for new users (first login)
  const [showOnboarding, setShowOnboarding] = useState(false);
  // Auto-show daily lesson once per day on dashboard mount
  const [autoLesson, setAutoLesson] = useState(null);

  useEffect(() => {
    if (user && user.onboarding_completed === false) {
      setShowOnboarding(true);
    }
  }, [user?.onboarding_completed]);

  useEffect(() => {
    // Fetch the daily lesson once and auto-pop if not viewed today,
    // but only AFTER onboarding is done so we don't stack modals.
    if (!user || user.onboarding_completed === false || showOnboarding) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/learning/daily-lesson');
        if (!cancelled && res.data?.lesson && !res.data.viewed_today) {
          setAutoLesson(res.data.lesson);
        }
      } catch (e) {
        console.warn('[Dashboard] daily-lesson auto-pop failed:', e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.onboarding_completed, showOnboarding]);

  useEffect(() => {
    fetchData();
    
    // Set up auto-refresh every 60 seconds
    const intervalId = setInterval(() => {
      if (autoRefresh) {
        fetchData(true); // silent refresh
      }
    }, 60000);

    return () => clearInterval(intervalId);
  }, [autoRefresh]);

  const fetchData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setIsUpdating(true);
      
      const [portfolioRes, stocksRes] = await Promise.all([
        api.get('/portfolio'),
        api.get('/stocks/list')
      ]);
      
      setPortfolio(portfolioRes.data);
      
      // Get quotes for top 4 stocks
      const symbols = stocksRes.data.symbols || stocksRes.data;
      const topStocksList = Array.isArray(symbols) ? symbols.slice(0, 4) : [];
      
      const stocksWithQuotes = await Promise.all(
        topStocksList.map(async (symbolOrStock) => {
          const symbol = typeof symbolOrStock === 'string' ? symbolOrStock : symbolOrStock.symbol;
          try {
            const quote = await api.get(`/stocks/${symbol}`);
            return { symbol, ...quote.data };
          } catch {
            return { symbol, name: symbol, price: 0, change: 0, change_percent: '0' };
          }
        })
      );
      
      setTopStocks(stocksWithQuotes);
      setLastUpdate(new Date());
    } catch (error) {
      if (!silent) {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
      setIsUpdating(false);
    }
  };

  const stats = [
    {
      icon: Wallet,
      label: 'Cash Balance',
      value: user?.balance || 0,
      format: (n) => fmtCcy(n),
      color: 'text-gold'
    },
    {
      icon: ChartLine,
      label: 'Portfolio Value',
      value: portfolio?.summary?.total_value || 0,
      format: (n) => fmtCcy(n),
      color: 'text-blue-400'
    },
    {
      icon: TrendUp,
      label: 'Total Profit/Loss',
      value: portfolio?.summary?.total_profit_loss || 0,
      format: (n) => fmtCcy(n),
      color: portfolio?.summary?.total_profit_loss >= 0 ? 'text-success' : 'text-destructive'
    },
    {
      icon: Wallet,
      label: 'Total Value',
      value: portfolio?.summary?.total_portfolio_value || 25000,
      format: (n) => fmtCcy(n),
      color: 'text-white'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen noise-bg">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <TeenVestLoader size={112} label="Loading dashboard..." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen noise-bg" data-testid="dashboard-page">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl md:text-5xl font-unbounded font-bold text-white mb-2" data-testid="dashboard-greeting">
                {getGreeting()}, {user?.nickname || user?.username}
                <span className="text-gold">.</span>
              </h1>
              <p className="text-lg text-zinc-400">Real-time investment overview • Updates every minute</p>
            </div>
            
            {/* Refresh controls */}
            <div className="flex items-center gap-4">
              <PriceUpdateIndicator isUpdating={isUpdating} lastUpdate={lastUpdate} />
              <button
                onClick={handleSharePortfolio}
                disabled={sharingPortfolio || (user?.is_guest)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 transition-colors disabled:opacity-50"
                data-testid="share-portfolio-button"
                title={user?.is_guest ? 'Sign up to share your portfolio' : 'Share your portfolio to the community feed'}
              >
                <Share size={18} weight="fill" />
                <span className="text-sm font-semibold">{sharingPortfolio ? 'Sharing...' : 'Share Portfolio'}</span>
              </button>
              <button
                onClick={() => fetchData()}
                disabled={isUpdating}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors disabled:opacity-50"
                data-testid="refresh-button"
              >
                <ArrowsClockwise 
                  size={20} 
                  className={isUpdating ? 'animate-spin' : ''} 
                />
                <span className="text-sm text-white">Refresh</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Real-time Profit Tracker */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12"
        >
          <RealTimeProfitTracker 
            portfolio={portfolio} 
            lastUpdate={lastUpdate}
            isUpdating={isUpdating}
          />
        </motion.div>

        {/* Quick Actions Row */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <QuickActions onAiClick={() => setShowAiModal(true)} />
        </motion.div>

        {/* Market Status + Top Movers row */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8"
        >
          <div className="lg:col-span-1">
            <MarketStatusWidget />
          </div>
          <div className="lg:col-span-2">
            <TopMoversWidget holdings={portfolio?.holdings || []} />
          </div>
        </motion.div>

        {/* AI Analyst prompt (pre-activation) */}
        {showAiPrompt && (
          <AIAnalystPromptCard
            onActivate={() => setShowAiModal(true)}
            onDismiss={handleAiDismiss}
          />
        )}

        {/* AI Analyst panel (post-activation) */}
        {aiStatus?.status === 'active' && aiAnalysis && (
          <AIAnalystPanel
            analysis={aiAnalysis}
            onRefreshed={setAiAnalysis}
          />
        )}

        {/* Daily Lesson + Quiz cards */}
        <DailyLearningCard />

        {/* Story Banner - Show if user has no holdings OR low cash */}
        {((!portfolio?.holdings || portfolio.holdings.length === 0) || (user?.balance ?? 0) < 1000) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-8 p-6 bg-gradient-to-r from-red-900/30 to-orange-900/30 border border-red-500/30 rounded-2xl"
            data-testid="empty-portfolio-prompt"
          >
            <div className="flex items-start gap-4">
              <div className="text-4xl">{(!portfolio?.holdings || portfolio.holdings.length === 0) ? '💸' : '⚠️'}</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-2">
                  {(!portfolio?.holdings || portfolio.holdings.length === 0)
                    ? "You haven't invested yet!"
                    : "Running low on cash?"}
                </h3>
                <p className="text-zinc-300 mb-3">
                  {(!portfolio?.holdings || portfolio.holdings.length === 0) ? (
                    <>You have <span className="text-gold font-bold">{fmtCcy(user?.balance || 0)}</span> sitting idle.
                    Every week without investing costs you <span className="text-red-400 font-bold">{fmtCcy(1000)}</span>.</>
                  ) : (
                    <>Your cash balance is <span className="text-gold font-bold">{fmtCcy(user?.balance || 0)}</span>.
                    Don&apos;t sell at a loss — earn <span className="text-emerald-400 font-bold">{fmtCcy(3000)}</span> per game mode and <span className="text-purple-400 font-bold">{fmtCcy(50)}</span> per perfect quiz!</>
                  )}
                </p>
                <p className="text-zinc-400 text-sm mb-4">
                  💡 <strong>Two ways to grow your portfolio:</strong> Buy real stocks in the Market, OR play Game Modes to earn cash + XP.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => navigate('/market')}
                    className="px-5 py-2.5 bg-gradient-to-r from-gold to-yellow-600 text-black font-bold rounded-xl hover:scale-105 transition-transform"
                    data-testid="prompt-go-market"
                  >
                    Start Investing →
                  </button>
                  <button
                    onClick={() => navigate('/game-modes')}
                    className="flex items-center gap-2 px-5 py-2.5 bg-white/5 hover:bg-white/10 text-white font-bold rounded-xl border border-white/10 transition-colors"
                    data-testid="prompt-go-gamemodes"
                  >
                    <GameController size={18} weight="fill" />
                    Earn From Game Modes
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-6"
                data-testid={`stat-card-${index}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <Icon size={32} weight="duotone" className={stat.color} />
                </div>
                <p className="text-sm text-zinc-400 uppercase tracking-wide mb-1">{stat.label}</p>
                <p className={`text-2xl font-mono font-bold ${stat.color}`}>
                  <AnimatedCounter value={stat.value} format={stat.format} />
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Portfolio Holdings — table + Top Gainers */}
        {portfolio?.holdings && portfolio.holdings.length > 0 && (
          <PortfolioTable holdings={portfolio.holdings} />
        )}

        {/* News flash for owned stocks */}
        <HoldingsNewsFeed hasHoldings={!!(portfolio?.holdings && portfolio.holdings.length > 0)} />

        {/* Top Stocks */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-unbounded font-bold text-white">Trending Stocks</h2>
            <button
              onClick={() => navigate('/market')}
              className="text-gold hover:text-gold-light font-bold transition-colors"
              data-testid="explore-market"
            >
              Explore Market →
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {topStocks.map((stock) => (
              <StockCard
                key={stock.symbol}
                stock={stock}
                onClick={() => navigate(`/market/${stock.symbol}`)}
              />
            ))}
          </div>
        </motion.div>
      </div>

      {showOnboarding && (
        <OnboardingModal onClose={() => setShowOnboarding(false)} />
      )}
      {autoLesson && !showOnboarding && (
        <DailyLessonModal lesson={autoLesson} onClose={() => setAutoLesson(null)} />
      )}
      <AIAnalystActivationModal
        open={showAiModal}
        onClose={() => setShowAiModal(false)}
        onActivated={handleAiActivated}
      />
    </div>
  );
};

export default Dashboard;